import React, { useState, useEffect } from 'react';
import { 
  FaArrowLeft, 
  FaExchangeAlt, 
  FaCheck, 
  FaColumns, 
  FaRandom,
  FaTag,
  FaHistory,
  FaCodeBranch,
  FaClock,
  FaChevronLeft,
  FaChevronRight,
  FaSave,
  FaChartBar,
  FaQuestionCircle
} from 'react-icons/fa';

// 版本比较组件，用于对比两个版本的差异
function VersionCompare({ prompt, onBack, onSwitchVersion, onCreateMergedVersion }) {
  const [leftVersionIndex, setLeftVersionIndex] = useState(prompt.currentVersionIndex > 0 ? prompt.currentVersionIndex - 1 : 0);
  const [rightVersionIndex, setRightVersionIndex] = useState(prompt.currentVersionIndex);
  const [viewMode, setViewMode] = useState('split'); // 'split' 或 'unified'
  const [differences, setDifferences] = useState([]);
  const [mergeContent, setMergeContent] = useState('');
  const [showMergeEditor, setShowMergeEditor] = useState(false);
  const [mergeVersionNumber, setMergeVersionNumber] = useState('');
  const [mergeDescription, setMergeDescription] = useState('');
  const [activeTab, setActiveTab] = useState('diff'); // 'diff' 或 'stats'

  // 格式化日期
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 计算文本差异
  useEffect(() => {
    if (!prompt || !prompt.versions) return;

    const leftContent = prompt.versions[leftVersionIndex].content;
    const rightContent = prompt.versions[rightVersionIndex].content;
    
    // 简单的行级差异比较
    const leftLines = leftContent.split('\n');
    const rightLines = rightContent.split('\n');
    
    const diffs = [];
    const maxLength = Math.max(leftLines.length, rightLines.length);
    
    for (let i = 0; i < maxLength; i++) {
      const leftLine = i < leftLines.length ? leftLines[i] : null;
      const rightLine = i < rightLines.length ? rightLines[i] : null;
      
      if (leftLine === rightLine) {
        diffs.push({ type: 'unchanged', left: leftLine, right: rightLine, lineNumber: i + 1 });
      } else {
        diffs.push({ 
          type: leftLine === null ? 'added' : rightLine === null ? 'removed' : 'changed',
          left: leftLine,
          right: rightLine,
          lineNumber: i + 1
        });
      }
    }
    
    setDifferences(diffs);
    setMergeContent(rightContent); // 默认使用右侧（较新）版本作为合并基础
  }, [prompt, leftVersionIndex, rightVersionIndex]);

  // 处理版本选择变更
  const handleVersionChange = (side, index) => {
    if (side === 'left') {
      setLeftVersionIndex(index);
    } else {
      setRightVersionIndex(index);
    }
  };

  // 交换左右版本
  const swapVersions = () => {
    const temp = leftVersionIndex;
    setLeftVersionIndex(rightVersionIndex);
    setRightVersionIndex(temp);
  };

  // 处理合并版本保存
  const handleMergeSave = () => {
    if (!mergeContent.trim()) {
      alert('合并内容不能为空');
      return;
    }

    onCreateMergedVersion({
      content: mergeContent,
      description: mergeDescription || `从版本${leftVersionIndex + 1}和版本${rightVersionIndex + 1}合并`,
      versionNumber: mergeVersionNumber || `${prompt.versions.length + 1}.0`,
      mergedFrom: [leftVersionIndex, rightVersionIndex]
    });
  };

  // 使用左侧版本的行
  const selectLeftLine = (lineIndex) => {
    const newContent = mergeContent.split('\n');
    newContent[lineIndex] = differences[lineIndex].left || '';
    setMergeContent(newContent.join('\n'));
  };

  // 使用右侧版本的行
  const selectRightLine = (lineIndex) => {
    const newContent = mergeContent.split('\n');
    newContent[lineIndex] = differences[lineIndex].right || '';
    setMergeContent(newContent.join('\n'));
  };

  // 导航到上一个或下一个版本
  const navigateVersion = (side, direction) => {
    if (side === 'left') {
      const newIndex = direction === 'prev' 
        ? Math.max(0, leftVersionIndex - 1) 
        : Math.min(prompt.versions.length - 1, leftVersionIndex + 1);
      if (newIndex !== leftVersionIndex) {
        setLeftVersionIndex(newIndex);
      }
    } else {
      const newIndex = direction === 'prev' 
        ? Math.max(0, rightVersionIndex - 1) 
        : Math.min(prompt.versions.length - 1, rightVersionIndex + 1);
      if (newIndex !== rightVersionIndex) {
        setRightVersionIndex(newIndex);
      }
    }
  };

  return (
    <div className="card-apple animate-fade-in bg-white rounded-xl shadow-lg overflow-hidden">
      {/* 顶部操作栏 */}
      <div className="flex justify-between items-center p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
        <button
          className="flex items-center text-gray-700 hover:text-blue-600 transition-all duration-200 px-3 py-1.5 rounded-md hover:bg-white/50"
          onClick={onBack}
        >
          <FaArrowLeft className="mr-2" />
          <span className="font-medium">返回详情</span>
        </button>
        
        <div className="flex space-x-2">
          <button
            className={`px-3 py-1.5 rounded-md flex items-center transition-all duration-200 ${
              viewMode === 'split' 
                ? 'bg-blue-100 text-blue-700 font-medium shadow-sm' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            onClick={() => setViewMode('split')}
          >
            <FaColumns className="mr-1.5" />
            并排视图
          </button>
          <button
            className={`px-3 py-1.5 rounded-md flex items-center transition-all duration-200 ${
              viewMode === 'unified' 
                ? 'bg-blue-100 text-blue-700 font-medium shadow-sm' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            onClick={() => setViewMode('unified')}
          >
            <FaRandom className="mr-1.5" />
            统一视图
          </button>
          <button
            className={`px-3 py-1.5 rounded-md flex items-center transition-all duration-200 ${
              showMergeEditor 
                ? 'bg-orange-100 text-orange-700 font-medium' 
                : 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:shadow-md'
            }`}
            onClick={() => setShowMergeEditor(!showMergeEditor)}
          >
            <FaCodeBranch className="mr-1.5" />
            {showMergeEditor ? '取消合并' : '创建合并版本'}
          </button>
        </div>
      </div>

      {/* 版本选择器 */}
      <div className="flex justify-between items-center p-4 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center space-x-4 flex-1">
          <div className="flex-1 bg-white rounded-lg shadow-sm p-2 border border-gray-200">
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-sm font-medium text-gray-700">左侧版本</label>
              <div className="text-xs px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded-full font-medium">版本 {leftVersionIndex + 1} / {prompt.versions.length}</div>
            </div>
            <div className="flex items-center">
              <button 
                className={`p-1 rounded-full ${leftVersionIndex === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:text-blue-600 hover:bg-blue-50'}`}
                onClick={() => navigateVersion('left', 'prev')}
                disabled={leftVersionIndex === 0}
              >
                <FaChevronLeft />
              </button>
              <div className="relative flex-1 mx-2">
                <select 
                  className="w-full py-1.5 pl-3 pr-8 text-sm border-0 focus:ring-2 focus:ring-blue-500 rounded-md bg-gray-50 cursor-pointer transition-all duration-200"
                  value={leftVersionIndex}
                  onChange={(e) => handleVersionChange('left', parseInt(e.target.value))}
                >
                  {prompt.versions.map((version, index) => (
                    <option key={`left-${version.id}`} value={index}>
                      版本 {index + 1} - {version.versionNumber || `v${index + 1}`} 
                      {index === prompt.currentVersionIndex ? ' (当前)' : ''}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <FaChevronRight className="rotate-90 text-gray-400" size={10} />
                </div>
              </div>
              <button 
                className={`p-1 rounded-full ${leftVersionIndex === prompt.versions.length - 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:text-blue-600 hover:bg-blue-50'}`}
                onClick={() => navigateVersion('left', 'next')}
                disabled={leftVersionIndex === prompt.versions.length - 1}
              >
                <FaChevronRight />
              </button>
            </div>
          </div>
          
          <button
            className="p-2 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-md hover:shadow-lg transform transition-all duration-200 hover:scale-110"
            onClick={swapVersions}
            title="交换左右版本"
          >
            <FaExchangeAlt />
          </button>
          
          <div className="flex-1 bg-white rounded-lg shadow-sm p-2 border border-gray-200">
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-sm font-medium text-gray-700">右侧版本</label>
              <div className="text-xs px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded-full font-medium">版本 {rightVersionIndex + 1} / {prompt.versions.length}</div>
            </div>
            <div className="flex items-center">
              <button 
                className={`p-1 rounded-full ${rightVersionIndex === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:text-blue-600 hover:bg-blue-50'}`}
                onClick={() => navigateVersion('right', 'prev')}
                disabled={rightVersionIndex === 0}
              >
                <FaChevronLeft />
              </button>
              <div className="relative flex-1 mx-2">
                <select 
                  className="w-full py-1.5 pl-3 pr-8 text-sm border-0 focus:ring-2 focus:ring-blue-500 rounded-md bg-gray-50 cursor-pointer transition-all duration-200"
                  value={rightVersionIndex}
                  onChange={(e) => handleVersionChange('right', parseInt(e.target.value))}
                >
                  {prompt.versions.map((version, index) => (
                    <option key={`right-${version.id}`} value={index}>
                      版本 {index + 1} - {version.versionNumber || `v${index + 1}`} 
                      {index === prompt.currentVersionIndex ? ' (当前)' : ''}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <FaChevronRight className="rotate-90 text-gray-400" size={10} />
                </div>
              </div>
              <button 
                className={`p-1 rounded-full ${rightVersionIndex === prompt.versions.length - 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:text-blue-600 hover:bg-blue-50'}`}
                onClick={() => navigateVersion('right', 'next')}
                disabled={rightVersionIndex === prompt.versions.length - 1}
              >
                <FaChevronRight />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 版本信息卡片 */}
      <div className="grid grid-cols-2 gap-4 p-4">
        <div className="card-apple card-hover shadow-soft p-4">
          <h3 className="font-medium text-gray-800 mb-2 flex items-center">
            <FaHistory className="mr-2 text-blue-600" size={14} />
            版本 {leftVersionIndex + 1} - {prompt.versions[leftVersionIndex].versionNumber || `v${leftVersionIndex + 1}`}
          </h3>
          <div className="text-sm text-gray-500 space-y-2">
            <div className="flex items-center">
              <FaClock className="mr-2 text-gray-400" size={12} />
              创建于: {formatDate(prompt.versions[leftVersionIndex].createdAt)}
            </div>
            {prompt.versions[leftVersionIndex].description && (
              <div className="flex items-center">
                <FaTag className="mr-2 text-gray-400" size={12} />
                描述: {prompt.versions[leftVersionIndex].description}
              </div>
            )}
          </div>
        </div>
        
        <div className="card-apple card-hover shadow-soft p-4">
          <h3 className="font-medium text-gray-800 mb-2 flex items-center">
            <FaHistory className="mr-2 text-blue-600" size={14} />
            版本 {rightVersionIndex + 1} - {prompt.versions[rightVersionIndex].versionNumber || `v${rightVersionIndex + 1}`}
          </h3>
          <div className="text-sm text-gray-500 space-y-2">
            <div className="flex items-center">
              <FaClock className="mr-2 text-gray-400" size={12} />
              创建于: {formatDate(prompt.versions[rightVersionIndex].createdAt)}
            </div>
            {prompt.versions[rightVersionIndex].description && (
              <div className="flex items-center">
                <FaTag className="mr-2 text-gray-400" size={12} />
                描述: {prompt.versions[rightVersionIndex].description}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 差异统计 */}
      <div className="p-4 border-t border-b border-gray-200 bg-gray-50">
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-700 space-x-4">
            <span className="inline-flex items-center">
              <span className="font-medium">总行数:</span> 
              <span className="ml-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">{differences.length}</span>
            </span>
            <span className="inline-flex items-center">
              <span className="font-medium">新增:</span> 
              <span className="ml-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                {differences.filter(d => d.type === 'added').length}
              </span>
            </span>
            <span className="inline-flex items-center">
              <span className="font-medium">删除:</span> 
              <span className="ml-1 px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                {differences.filter(d => d.type === 'removed').length}
              </span>
            </span>
            <span className="inline-flex items-center">
              <span className="font-medium">修改:</span> 
              <span className="ml-1 px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
                {differences.filter(d => d.type === 'changed').length}
              </span>
            </span>
          </div>
          
          <div className="flex space-x-2">
            <button 
              className={`px-2 py-1 text-sm rounded flex items-center ${activeTab === 'diff' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-200'}`}
              onClick={() => setActiveTab('diff')}
            >
              <FaHistory className="mr-1" size={12} />
              差异
            </button>
            <button 
              className={`px-2 py-1 text-sm rounded flex items-center ${activeTab === 'stats' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-200'}`}
              onClick={() => setActiveTab('stats')}
            >
              <FaChartBar className="mr-1" size={12} />
              统计
            </button>
          </div>
        </div>
      </div>

      {/* 合并编辑器 */}
      {showMergeEditor && (
        <div className="p-4 bg-gray-50 border-b border-gray-200 animate-slide-in">
          <h3 className="font-medium text-gray-800 mb-4 flex items-center">
            <FaCodeBranch className="mr-2 text-blue-600" />
            创建合并版本
          </h3>
          
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">版本号</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="例如: 3.0"
                value={mergeVersionNumber}
                onChange={(e) => setMergeVersionNumber(e.target.value)}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">描述</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="描述此合并版本的变更"
                value={mergeDescription}
                onChange={(e) => setMergeDescription(e.target.value)}
              />
            </div>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">合并内容</label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-64 font-mono text-sm"
              value={mergeContent}
              onChange={(e) => setMergeContent(e.target.value)}
            />
          </div>
          
          <div className="flex justify-end">
            <button
              className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-md shadow hover:shadow-md transition-all duration-200 flex items-center"
              onClick={handleMergeSave}
            >
              <FaSave className="mr-2" />
              保存合并版本
            </button>
          </div>
        </div>
      )}

      {/* 差异比较视图 */}
      <div className="p-4">
        <div className={`${viewMode === 'split' ? 'grid grid-cols-2 gap-4' : ''}`}>
          {viewMode === 'split' ? (
            // 并排视图
            <>
              <div className="overflow-hidden rounded-lg border border-gray-200 shadow-sm">
                <div className="bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 flex items-center border-b border-gray-200">
                  <FaHistory className="mr-2 text-blue-600" size={12} />
                  版本 {leftVersionIndex + 1}
                </div>
                <div className="diff-container p-4 bg-white overflow-auto max-h-[500px] font-mono text-sm">
                  {differences.map((diff, index) => (
                    <div 
                      key={`left-${index}`} 
                      className={`py-1 px-2 rounded ${
                        diff.type === 'added' ? 'text-gray-300' : 
                        diff.type === 'removed' ? 'bg-red-50 text-red-800 border-l-4 border-red-500' : 
                        diff.type === 'changed' ? 'bg-yellow-50 text-yellow-800 border-l-4 border-yellow-500' : 
                        ''
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="mr-2 text-gray-400 select-none">{diff.lineNumber}</span>
                          {diff.left || '(空行)'}
                        </div>
                        {showMergeEditor && diff.type !== 'added' && (
                          <button
                            className="p-1 rounded-full text-blue-600 hover:bg-blue-100 transition-colors"
                            onClick={() => selectLeftLine(index)}
                            title="使用此行"
                          >
                            <FaCheck size={12} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="overflow-hidden rounded-lg border border-gray-200 shadow-sm">
                <div className="bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 flex items-center border-b border-gray-200">
                  <FaHistory className="mr-2 text-blue-600" size={12} />
                  版本 {rightVersionIndex + 1}
                </div>
                <div className="diff-container p-4 bg-white overflow-auto max-h-[500px] font-mono text-sm">
                  {differences.map((diff, index) => (
                    <div 
                      key={`right-${index}`} 
                      className={`py-1 px-2 rounded ${
                        diff.type === 'removed' ? 'text-gray-300' : 
                        diff.type === 'added' ? 'bg-green-50 text-green-800 border-l-4 border-green-500' : 
                        diff.type === 'changed' ? 'bg-yellow-50 text-yellow-800 border-l-4 border-yellow-500' : 
                        ''
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="mr-2 text-gray-400 select-none">{diff.lineNumber}</span>
                          {diff.right || '(空行)'}
                        </div>
                        {showMergeEditor && diff.type !== 'removed' && (
                          <button
                            className="p-1 rounded-full text-blue-600 hover:bg-blue-100 transition-colors"
                            onClick={() => selectRightLine(index)}
                            title="使用此行"
                          >
                            <FaCheck size={12} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            // 统一视图
            <div className="overflow-hidden rounded-lg border border-gray-200 shadow-sm">
              <div className="bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 flex items-center border-b border-gray-200 justify-between">
                <div className="flex items-center">
                  <FaHistory className="mr-2 text-blue-600" size={12} />
                  统一差异视图
                </div>
                {showMergeEditor && (
                  <div className="flex items-center text-xs text-gray-500">
                    <FaQuestionCircle className="mr-1" />
                    点击行内的 <FaCheck className="mx-1 text-blue-600" size={10} /> 按钮选择要使用的内容
                  </div>
                )}
              </div>
              <div className="diff-container p-4 bg-white overflow-auto max-h-[500px] font-mono text-sm">
                {differences.map((diff, index) => (
                  <div key={`unified-${index}`} className="mb-2">
                    <div className="text-gray-400 text-xs mb-1">行 {diff.lineNumber}</div>
                    {diff.type !== 'unchanged' && (
                      <div className="grid grid-cols-2 gap-2">
                        <div className="relative">
                          <div 
                            className={`py-1 px-2 rounded ${
                              diff.type === 'added' ? 'text-gray-400' : 
                              diff.type === 'removed' ? 'bg-red-50 text-red-800 border-l-4 border-red-500' : 
                              diff.type === 'changed' ? 'bg-red-50 text-red-800 border-l-4 border-red-500' : 
                              ''
                            }`}
                          >
                            {diff.left || '(空行)'}
                          </div>
                          {showMergeEditor && diff.type !== 'added' && (
                            <button
                              className="absolute right-1 top-1 p-1 rounded-full text-blue-600 hover:bg-blue-100 transition-colors bg-white bg-opacity-80"
                              onClick={() => selectLeftLine(index)}
                              title="使用此行"
                            >
                              <FaCheck size={12} />
                            </button>
                          )}
                        </div>
                        <div className="relative">
                          <div 
                            className={`py-1 px-2 rounded ${
                              diff.type === 'removed' ? 'text-gray-400' : 
                              diff.type === 'added' ? 'bg-green-50 text-green-800 border-l-4 border-green-500' : 
                              diff.type === 'changed' ? 'bg-green-50 text-green-800 border-l-4 border-green-500' : 
                              ''
                            }`}
                          >
                            {diff.right || '(空行)'}
                          </div>
                          {showMergeEditor && diff.type !== 'removed' && (
                            <button
                              className="absolute right-1 top-1 p-1 rounded-full text-blue-600 hover:bg-blue-100 transition-colors bg-white bg-opacity-80"
                              onClick={() => selectRightLine(index)}
                              title="使用此行"
                            >
                              <FaCheck size={12} />
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                    {diff.type === 'unchanged' && (
                      <div className="py-1 px-2 text-gray-700">
                        {diff.left}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 添加自定义样式 */}
      <style jsx>{`
        .animate-fade-in {
          animation: fadeIn 0.3s ease-in-out;
        }
        
        .animate-slide-in {
          animation: slideIn 0.3s ease-in-out;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideIn {
          from { transform: translateY(-10px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        
        .hover-lift {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        
        .hover-lift:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }
        
        .diff-container {
          font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
        }
      `}</style>
    </div>
  );
}

export default VersionCompare;
